import { Minus, Plus, Sparkles } from "lucide-react";
import { useContext, useEffect, useState } from "react";

import SkillsEditorListItems, {
	type GroupedSkill,
	type SkillListItem,
} from "@/app/components/ResumeBuilder/SkillsEditorListItems/SkillsEditorListItems";
import TextEditor from "@/app/components/TextEditor";
import { Input } from "@/app/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/components/ui/popover";
import ResumeContext from "@/app/context/ResumeContext";
import { getFirstWordSeparedByFullColon } from "@/app/handlers/utilityFunctions";
import { skillsForJobFunction } from "@/app/seed/skills/skillsMapping";
import type { ResumeContextValue } from "@/app/types/resume";
import GroupedSkillsItem from "../../Skills/GroupedSkillsItem";

interface RelevantSkillsPickerProps {
	jobTitle: string;
	employer: string;
	groupedSkills: GroupedSkill[];
	setGroupedSkills: (skills: GroupedSkill[]) => void;
	customSkills: string;
	setCustomSkills: (value: string) => void;
}

/**
 * The skills applied in one job, chosen from a popover so the categories they
 * build up can have the page's full width.
 */
const RelevantSkillsPicker = ({
	jobTitle,
	employer,
	groupedSkills,
	setGroupedSkills,
	customSkills,
	setCustomSkills,
}: RelevantSkillsPickerProps) => {
	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;

	const hasSavedSkills = groupedSkills.length > 0 || customSkills !== "";
	const [expanded, setExpanded] = useState(hasSavedSkills);
	const [addCustomCategory, setAddCustomCategory] = useState(customSkills !== "");
	const [skillList, setSkillList] = useState<SkillListItem[]>([]);
	const [filteredSkillList, setFilteredSkillList] = useState<SkillListItem[]>([]);
	// The picker writes the chosen skills here as a list. Only `groupedSkills` and
	// `customSkills` are saved; this is what tells the list which rows are ticked.
	const [pickedSkills, setPickedSkills] = useState("");

	// Both open once, when an edited job's saved skills arrive. Collapsing after
	// that does not reopen them, because the value they watch has not changed.
	useEffect(() => {
		if (hasSavedSkills) setExpanded(true);
	}, [hasSavedSkills]);

	useEffect(() => {
		if (customSkills !== "") setAddCustomCategory(true);
	}, [customSkills]);

	useEffect(() => {
		const jobFunction = resumeData.contactInfo.resumeFunction.toString().toLowerCase();

		setSkillList(
			skillsForJobFunction(jobFunction)
				.filter((title) => title !== "")
				.map((title, id) => ({ id, title, added: false })),
		);
	}, [resumeData.contactInfo.resumeFunction]);

	useEffect(() => {
		setFilteredSkillList(skillList);
	}, [skillList]);

	const collapseGroupedSkills = (groupedSkillIndex: number) => {
		setGroupedSkills(
			groupedSkills.map((skillGroup, index) => ({
				...skillGroup,
				collapsed: index === groupedSkillIndex ? !skillGroup.collapsed : skillGroup.collapsed,
			})),
		);
	};

	const removeGroupedSkills = (skillTitle: string) => {
		setGroupedSkills(groupedSkills.filter((skillItem) => skillItem.title !== skillTitle));

		// Immutable update: new array + new object for the changed item, so current
		// state is not mutated and React sees a fresh reference.
		setFilteredSkillList((previous) =>
			previous.map((item) =>
				getFirstWordSeparedByFullColon(item.title) === skillTitle ? { ...item, added: false } : item,
			),
		);
	};

	const withCopiedSkills = () =>
		groupedSkills.map((group) => ({ ...group, skills: group.skills.map((skill) => ({ ...skill })) }));

	const onRemoveSingleSkill = (groupedSkillIndex: number, singleSkillIndex: number) => {
		const updated = withCopiedSkills();
		updated[groupedSkillIndex].skills = updated[groupedSkillIndex].skills.filter(
			(_, index) => index !== singleSkillIndex,
		);
		setGroupedSkills(updated);
	};

	const onAddCustomSkill = (groupedSkillIndex: number) => {
		const updated = withCopiedSkills();
		updated[groupedSkillIndex].skills = [...updated[groupedSkillIndex].skills, { added: true, skill: "" }];
		setGroupedSkills(updated);
	};

	const onSaveCustomSkillAdded = (groupedSkillIndex: number, singleSkillIndex: number, skillAdded: string) => {
		const updated = withCopiedSkills();
		updated[groupedSkillIndex].skills[singleSkillIndex].skill = skillAdded;
		setGroupedSkills(updated);
	};

	const toggleEditorVisibility = () => {
		setGroupedSkills(withCopiedSkills().map((group) => ({ ...group, collapsed: !addCustomCategory })));
		setAddCustomCategory(!addCustomCategory);
	};

	return (
		<div>
			<button
				type="button"
				className="flex cursor-pointer items-center gap-1 text-left"
				onClick={() => setExpanded(!expanded)}
			>
				{expanded ? <Minus className="size-4" /> : <Plus className="size-4" />}
				{/* Named once the job is: "as a  at " read as broken text while the title
				    and employer above were still empty. */}
				<span className="text-sm">
					{jobTitle && employer ? (
						<>
							Highlight relevant skills you applied as a{" "}
							<span className="font-semibold text-primary-700">{jobTitle}</span> at{" "}
							<span className="font-semibold text-primary-700">{employer}</span>
						</>
					) : (
						"Highlight the relevant skills you applied in this job"
					)}
				</span>
			</button>

			{expanded && (
				<div className="mt-3">
					<Popover>
						<PopoverTrigger asChild>
							<button
								type="button"
								className="flex cursor-pointer items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 font-semibold text-primary-700 text-sm"
							>
								<Sparkles className="size-4" />
								Pick skills for this job
							</button>
						</PopoverTrigger>
						<PopoverContent
							align="start"
							collisionPadding={16}
							className="max-h-(--radix-popover-content-available-height) w-[min(32rem,92vw)] p-2"
						>
							<Input
								type="text"
								onChange={(event) => {
									const search = event.target.value.trim().toLowerCase();
									// Filtered off the full list, never off what is already on screen:
									// narrowing the shown list left the removed rows unreachable once
									// the search box was cleared again.
									setFilteredSkillList(
										search === "" ? skillList : skillList.filter((skill) => skill.title.toLowerCase().includes(search)),
									);
								}}
								placeholder="Search skills"
							/>
							<div className="min-h-0 flex-1 overflow-y-auto">
								<SkillsEditorListItems
									editorListItems={filteredSkillList}
									editorContent={pickedSkills}
									setEditorContent={setPickedSkills}
									setEditorListItems={setFilteredSkillList}
									groupedSkills={groupedSkills}
									setGroupedSkills={setGroupedSkills}
								/>
							</div>
						</PopoverContent>
					</Popover>

					<div className="mt-3">
						{groupedSkills.map((item, groupedSkillIndex) => {
							if (item.title === "") return null;

							return (
								<GroupedSkillsItem
									key={item.title}
									item={item}
									collapseGroupedSkills={collapseGroupedSkills}
									groupedSkillIndex={groupedSkillIndex}
									removeGroupedSkills={removeGroupedSkills}
									onSaveCustomSkillAdded={onSaveCustomSkillAdded}
									onRemoveSingleSkill={onRemoveSingleSkill}
									onAddCustomSkill={onAddCustomSkill}
								/>
							);
						})}

						{addCustomCategory ? (
							<TextEditor
								value={customSkills}
								setValue={setCustomSkills}
								placeholder="Add custom skills..."
								toggleEditorVisibility={toggleEditorVisibility}
								className="text-editor-auto-grow"
							/>
						) : (
							<button type="button" className="flex cursor-pointer items-center gap-2" onClick={toggleEditorVisibility}>
								<Plus className="size-4" />
								<span className="font-medium text-sm">Add custom category</span>
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default RelevantSkillsPicker;
